// Constant imports:
import { GET_SIGTI_API_URL } from '../../config';
import { addBookmarkQuery, deleteBookmarkQuery, editBookmarkQuery, requestBookmarksQuery } from '../queries/bookmarkQueries';
// Type imports:
import { AppDispatch } from '../../index'; // Use the root dispatch type from your store
import { IBookmark } from '../../types/api/BookmarkTypes';
import { BookmarkActions, ADD_BOOKMARK_FAILURE, ADD_BOOKMARK_SUCCESS, DELETE_BOOKMARK_FAILURE, DELETE_BOOKMARK_SUCCESS, EDIT_BOOKMARK_FAILURE, EDIT_BOOKMARK_SUCCESS, REQUEST_BOOKMARKS_FAILURE, REQUEST_BOOKMARKS_SUCCESS, REQUEST_BOOKMARKS_WAITING } from '../../types/redux/BookmarkTypes';
import { bugsnagNotify, fetchResponseToDetailedString } from '../../services/bugsnagUtils';
import { REQUEST_FORBIDDEN } from '../../types/redux/LoginTypes';

// ================================ CHECK IF FORBIDDEN RESOURCE (LOGOUT IF TRUE) ================================
const checkIfForbiddenResource = (responseBody: any) => {
    if (responseBody.errors && responseBody.errors[0].message === "Forbidden resource") {
        return true;
    }
    return false;
}

const requestForbidden = () => ({
    type: REQUEST_FORBIDDEN,
});

/* REQUEST BOOKMARKS */
const requestBookmarksThunk = async (dispatch: AppDispatch, accessToken: string) => {
    // Set waiting.
    dispatch(requestBookmarksWaiting());

    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestBookmarksQuery();
    const queryObject = {
        query,
        variables: {}
    }

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();

            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { bookmarks } = responseBody.data;
            dispatch(requestBookmarksSuccess(bookmarks));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Bookmarks', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestBookmarksFailure('Request Failed'));
        }
    } catch (e) {
        if ((e as Error).message.includes('Forbidden')) {
            dispatch(requestForbidden());
            console.log('Token expired. Logging out...');
            return;
        }
        bugsnagNotify(e as Error);
        dispatch(requestBookmarksFailure('Network Error'));
    }
}

const requestBookmarksWaiting = (): BookmarkActions => ({
    type: REQUEST_BOOKMARKS_WAITING
});

const requestBookmarksSuccess = (bookmarks: Array<IBookmark>): BookmarkActions => ({
    type: REQUEST_BOOKMARKS_SUCCESS,
    payload: {
        bookmarks
    }
});

const requestBookmarksFailure = (error: string): BookmarkActions => ({
    type: REQUEST_BOOKMARKS_FAILURE,
    payload: {
        error
    }
});

export const requestBookmarks = (accessToken: string) => (dispatch: AppDispatch) => requestBookmarksThunk(dispatch, accessToken);


/* ADD BOOKMARK */
const addBookmarkThunk = async (dispatch: AppDispatch, symbol: string, timestamp: string, comment: string, accessToken: string) => {
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = addBookmarkQuery();

    const bookmarkInput = {
        symbol,
        bmcomment: comment,
        timestamp: new Date(timestamp)
      }

    const queryObject = {
        query,
        variables: {
            bookmarkInput
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { addBookmark } = responseBody.data;
            dispatch(addBookmarkSuccess(addBookmark));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Add Bookmark', response);
            bugsnagNotify(new Error(errorString));
            dispatch(addBookmarkFailure('Request Failed'));
        }
    } catch (e) {
        if ((e as Error).message.includes('Forbidden')) {
            dispatch(requestForbidden());
            console.log('Token expired. Logging out...');
            return;
        }
        bugsnagNotify(e as Error);
        dispatch(addBookmarkFailure('Network Error'));
    }
}

const addBookmarkSuccess = (bookmark: IBookmark): BookmarkActions => ({
    type: ADD_BOOKMARK_SUCCESS,
    payload: {
        bookmark
    }
})

const addBookmarkFailure = (error: string): BookmarkActions => ({
    type: ADD_BOOKMARK_FAILURE,
    payload: {
        error
    }
})

export const addBookmark =
    (symbol: string, timestamp: string, comment: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    addBookmarkThunk(dispatch, symbol, timestamp, comment, accessToken);


/* EDIT BOOKMARK */
const editBookmarkThunk = async (dispatch: AppDispatch, accessToken: string, bookmarkId: number, comment: string) => {
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = editBookmarkQuery();

    const bookmarkInput = {
        id: bookmarkId,
        bmcomment: comment
      }

    const queryObject = {
        query,
        variables: {
            bookmarkInput
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { editBookmark: editedBookmark } = responseBody.data;
            dispatch(editBookmarkSuccess(editedBookmark));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Edit Bookmark', response);
            bugsnagNotify(new Error(errorString));
            dispatch(editBookmarkFailure('Request Failed'));
        }
    } catch (e) {
        if ((e as Error).message.includes('Forbidden')) {
            dispatch(requestForbidden());
            console.log('Token expired. Logging out...');
            return;
        }
        bugsnagNotify(e as Error);
        dispatch(editBookmarkFailure('Network Error'));
    }
}

const editBookmarkSuccess = (bookmark: IBookmark): BookmarkActions => ({
    type: EDIT_BOOKMARK_SUCCESS,
    payload: {
        bookmark
    }
})

const editBookmarkFailure = (error: string): BookmarkActions => ({
    type: EDIT_BOOKMARK_FAILURE,
    payload: {
        error
    }
})

export const editBookmark =
    (accessToken: string, bookmarkId: number, comment: string) =>
    (dispatch: AppDispatch) =>
    editBookmarkThunk(dispatch, accessToken, bookmarkId, comment);


/* DELETE BOOKMARK */
const deleteBookmarkThunk = async (dispatch: AppDispatch, accessToken: string, bookmarkId: number) => {
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = deleteBookmarkQuery()

    const bookmarkInput = {
        id: bookmarkId
    };

    const queryObject = {
        query,
        variables: {
            bookmarkInput
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { deleteBookmark: deletedBookmark } = responseBody.data;
            dispatch(deleteBookmarkSuccess(deletedBookmark));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Delete Bookmark', response);
            bugsnagNotify(new Error(errorString));
            dispatch(deleteBookmarkFailure('Request Failed'));
        }
    } catch (e) {
        if ((e as Error).message.includes('Forbidden')) {
            dispatch(requestForbidden());
            console.log('Token expired. Logging out...');
            return;
        }
        bugsnagNotify(e as Error);
        dispatch(deleteBookmarkFailure('Network Error'));
    }
}

const deleteBookmarkSuccess = (bookmark: IBookmark): BookmarkActions => ({
    type: DELETE_BOOKMARK_SUCCESS,
    payload: {
        bookmark
    }
})

const deleteBookmarkFailure = (error: string): BookmarkActions => ({
    type: DELETE_BOOKMARK_FAILURE,
    payload: {
        error
    }
})

export const deleteBookmark =
    (accessToken: string, bookmarkId: number) =>
    (dispatch: AppDispatch) =>
    deleteBookmarkThunk(dispatch, accessToken, bookmarkId);